import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/components/errorHandler/Actions';
import * as parentPasswordModalAction from '@/components/modals/parentPasswordModal/Actions';
import * as learningPathActions from '@/pages/learningPath/Actions';
import * as sharedActions from '@/shared/Actions';
import { STICKER_REWARDS } from '@/components/stickers/Constants';
import { learningPathActionTypes, TILE_STATUSES } from '@/pages/learningPath/Constants';
import {
	FRONTEND_ROUTES,
	getCollectRewardPath,
	getUpdateUserPath,
	ROUTES_API,
	SURVEY_CODES,
} from '@/services/Constants';
import { PLAY_MODE_TYPES } from '@/shared/Constants';
import { isEmptyLearningPath, updateLearningPath } from '@/pages/learningPath/Helper';
import { isLearningPath } from '@/shared/Helpers';
import { getUserId, getUserPlayMode } from '@/pages/learningPath/Selectors';
import { processRequest } from '@/services/Api';
import { historyPush } from '@/services/History';

// Root saga
export default function* (): Generator {
	yield all([
		takeLatest(learningPathActionTypes.GET_LEARNING_PATH_REQUEST, handleGetLearningPathRequest),
		takeLatest(
			learningPathActionTypes.UPDATE_ONBOARDING_DATA_REQUEST,
			handleUpdateOnboardingDataRequest,
		),
		takeLatest(
			learningPathActionTypes.SEND_COLLECTED_REWARD_REQUEST,
			handleSendCollectedRewardRequest,
		),
		takeLatest(
			learningPathActionTypes.RECEIVED_LEARNING_PATH_CHANNEL_STREAM,
			handleReceivedLearningPathChannelStream,
		),
		takeLatest(
			learningPathActionTypes.GET_AUTONOMY_EXPERIMENT_REQUEST,
			handleGetAutonomyExperimentRequest,
		),
	]);
}

// Saga to handle GET_LEARNING_PATH_REQUEST
export function* handleGetLearningPathRequest(): Generator {
	let userPlayMode = null;

	try {
		userPlayMode = yield select(getUserPlayMode);

		let learningPathResponse = [];

		const { data } = yield call(processRequest, ROUTES_API.USER_LEARNING_PATHS);
		learningPathResponse = data;

		if (
			!learningPathResponse.error &&
			isEmptyLearningPath(learningPathResponse?.learning_path)
		) {
			yield call(processRequest, ROUTES_API.GENERATE_USER_LEARNING_PATH, 'POST');
			const { data } = yield call(processRequest, ROUTES_API.USER_LEARNING_PATHS);
			learningPathResponse = data;
		}

		if (
			!learningPathResponse.error &&
			isEmptyLearningPath(learningPathResponse?.learning_path)
		) {
			yield put(errorHandlerActions.handleError(new Error('Empty learning path')));
			return;
		}

		if (learningPathResponse.learning_path) {
			learningPathResponse.learning_path = updateLearningPath(
				learningPathResponse.learning_path,
				userPlayMode === PLAY_MODE_TYPES.FREE_PLAY_AFTER_DAILY_GOAL,
			);
			yield put(learningPathActions.getLearningPathSuccess(learningPathResponse));
		}
	} catch (e: any) {
		yield put(errorHandlerActions.handleError(e));
		yield put(learningPathActions.getLearningPathError());
	}
}

// Saga to handle UPDATE_ONBOARDING_DATA_REQUEST
export function* handleUpdateOnboardingDataRequest({ payload }: any): Generator {
	try {
		const { key, status } = payload || {};

		const userId = yield select(getUserId);

		yield call(processRequest, getUpdateUserPath(userId), 'PUT', {
			user: { [key]: true },
		});

		const { data: userData } = yield call(processRequest, ROUTES_API.GET_USER_DATA);
		yield put(sharedActions.getUserDataSuccess(userData));

		if (status === TILE_STATUSES.TILE_IN_PROGRESS) {
			const { data } = yield call(processRequest, ROUTES_API.USER_LEARNING_PATHS);
			data.learning_path = updateLearningPath(
				data.learning_path,
				userData.play_mode === PLAY_MODE_TYPES.FREE_PLAY_AFTER_DAILY_GOAL,
			);
			yield put(learningPathActions.getLearningPathSuccess(data));
			yield put(learningPathActions.setFinishTileAnimation(true));
		}
	} catch (e: any) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

// Saga to handle SEND_COLLECTED_REWARD_REQUEST
export function* handleSendCollectedRewardRequest({ payload }: any): Generator {
	try {
		const { id, body, rewardType } = payload || {};
		const userPlayMode = yield select(getUserPlayMode);

		yield call(processRequest, getCollectRewardPath(id), 'POST', body);

		const { data } = yield call(processRequest, ROUTES_API.USER_LEARNING_PATHS);

		data.learning_path = updateLearningPath(
			data.learning_path,
			userPlayMode === PLAY_MODE_TYPES.FREE_PLAY_AFTER_DAILY_GOAL,
		);

		if (rewardType === STICKER_REWARDS.SHORTER_LEARNING_PATH) {
			yield put(learningPathActions.setNewSteps(data.learning_path));
			yield put(learningPathActions.setShorterLearningPathAnimation(true));
		} else if (rewardType === STICKER_REWARDS.SKIP_TILE) {
			yield put(learningPathActions.getLearningPathSuccess(data));
			yield put(learningPathActions.setSkipTileAnimation(true));
		} else {
			yield put(learningPathActions.getLearningPathSuccess(data));
			yield put(learningPathActions.setFinishTileAnimation(true));
		}
	} catch (e: any) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

// Saga to handle RECEIVED_LEARNING_PATH_CHANNEL_STREAM
export function* handleReceivedLearningPathChannelStream({ payload }: any): Generator {
	const { data } = payload || {};
	try {
		const userPlayMode = yield select(getUserPlayMode);

		data.learning_path = updateLearningPath(
			data.learning_path,
			userPlayMode === PLAY_MODE_TYPES.FREE_PLAY_AFTER_DAILY_GOAL,
		);

		yield put(learningPathActions.receivedLearningPathChannelStreamSuccess(data));
	} catch (e: any) {
		yield put(errorHandlerActions.handleError(e));
	}
}

// Saga to handle GET_AUTONOMY_EXPERIMENT_REQUEST
export function* handleGetAutonomyExperimentRequest(): Generator {
	try {
		yield call(processRequest, ROUTES_API.AUTONOMY_EXPERIMENT);

		yield put(learningPathActions.setTypeformSurvey(SURVEY_CODES.AUTONOMY_EXPERIMENT_FEEDBACK));

		yield put(learningPathActions.getAutonomyExperimentSuccess());
	} catch (e: any) {
		yield put(learningPathActions.getAutonomyExperimentError());
		yield put(errorHandlerActions.handleError(e));
	}
}
